import React, { useState } from 'react'
import { RouteComponentProps } from 'wouter'
import * as UI from '@mantine/core'

import { buildComponent } from '../../../components/factory'
import { graphql } from '../../../services/graphql'
import { serviceModals } from '../../../services/modals'
import { useOnMount } from '../../../hooks/use-on-mount'
import { useStoreState } from '../../../hooks/state'

// Écran « Administrateurs » (#7512) : il n'existait aucune vue des comptes
// back-office, ni aucun moyen d'en créer un — ils étaient insérés à la main en
// base puis amorcés avec un mot de passe partagé. Cet écran remplace ce
// mécanisme par une invitation nominative, et porte la réinitialisation du
// second facteur, qui tient lieu de codes de secours.

type Account = {
  _id: string
  email: string
  active: boolean
  // Compte créé dont l'invitation n'a pas encore été suivie : il n'a donc pas
  // encore de mot de passe, ni de second facteur.
  pending: boolean
  totpEnabled: boolean
  totpVerifiedAt: string | null
  trustedDevices: number
}

type Invitation = { email: string; url: string; emailSent: boolean }

// Validation avant envoi : les requêtes du service GraphQL sont interpolées
// sans échappement, une adresse contenant un guillemet casserait la requête.
const EMAIL = /^[^@\s"\\]+@[^@\s"\\]+\.[^@\s"\\]+$/

const formatDate = (value: string | null) => {
  if (!value) {
    return ''
  }

  const date = new Date(value)

  return Number.isNaN(date.valueOf()) ? '' : date.toLocaleDateString('fr-FR')
}

export const ScreenConnectedAdministrators =
  buildComponent<RouteComponentProps>()
    .withLocale('screens.connected.administrators.screen')
    .withLifecycle(() => {
      // Identifiant du compte connecté : l'API refuse l'auto-suppression, autant
      // ne pas proposer un bouton qui échouera.
      const moi = useStoreState((store) => store.utils.session.claims._id)
      const [accounts, setAccounts] = useState<Account[]>([])
      const [loading, setLoading] = useState(true)
      // `text` est déjà la chaîne finale : certains messages sont interpolés.
      const [notice, setNotice] = useState<{
        color: 'green' | 'red'
        text: string
      } | null>(null)

      const [email, setEmail] = useState('')
      const [busy, setBusy] = useState(false)
      const [copied, setCopied] = useState(false)
      // Dernière invitation émise. Affichée jusqu'à fermeture explicite :
      // quand l'email ne part pas, ce lien est le seul moyen d'atteindre le
      // destinataire.
      const [invitation, setInvitation] = useState<Invitation | null>(null)

      const load = () =>
        graphql.queries
          .getAdminAccounts()
          .then(({ administratorAdminAccounts }) => {
            setAccounts(administratorAdminAccounts || [])
            setLoading(false)
          })

      useOnMount(() => {
        load().catch((err) => {
          console.error(err)
          setLoading(false)
          setNotice({ color: 'red', text: 'loadError' })
        })
      })

      return {
        moi,
        accounts,
        loading,
        notice,
        setNotice,
        load,
        email,
        setEmail,
        busy,
        setBusy,
        invitation,
        setInvitation,
        copied,
        setCopied,
        copyInvitation: () => {
          if (!invitation) {
            return
          }
          navigator.clipboard?.writeText(invitation.url).catch(() => {})
          setCopied(true)
        },
        emailIsValid: EMAIL.test(email.trim()),
      }
    })
    .withRender(({ lifecycle, locale }) => {
      const fail = (key: string) =>
        lifecycle.setNotice({ color: 'red', text: locale(key as 'resetError') })

      // Création et ré-invitation partagent leur forme de retour.
      const handleInvitation = (
        result: {
          email: string | null
          invitationUrl: string | null
          emailSent: boolean | null
          error: string | null
        },
        fallbackEmail: string,
      ) => {
        if (result.error || !result.invitationUrl) {
          fail(result.error || 'createError')
          return false
        }

        const address = result.email || fallbackEmail

        lifecycle.setCopied(false)
        lifecycle.setInvitation({
          email: address,
          url: result.invitationUrl,
          emailSent: !!result.emailSent,
        })
        lifecycle.setNotice({
          color: result.emailSent ? 'green' : 'red',
          text: locale(
            result.emailSent ? 'invitationSent' : 'invitationNotSent',
            { email: address },
          ),
        })

        return true
      }

      const createAccount = async (event: React.FormEvent) => {
        event.preventDefault()

        if (!lifecycle.emailIsValid) {
          fail('invalid_email')
          return
        }

        lifecycle.setBusy(true)

        try {
          const { administratorCreateAdminAccount } =
            await graphql.mutations.createAdminAccount(lifecycle.email.trim())

          if (
            handleInvitation(
              administratorCreateAdminAccount,
              lifecycle.email.trim(),
            )
          ) {
            lifecycle.setEmail('')
            await lifecycle.load()
          }
        } catch (err) {
          console.error(err)
          fail('createError')
        } finally {
          lifecycle.setBusy(false)
        }
      }

      const resendInvitation = async (account: Account) => {
        lifecycle.setBusy(true)

        try {
          const { administratorInviteAdminAccount } =
            await graphql.mutations.inviteAdminAccount(account._id)

          handleInvitation(administratorInviteAdminAccount, account.email)
        } catch (err) {
          console.error(err)
          fail('createError')
        } finally {
          lifecycle.setBusy(false)
        }
      }

      const supprimer = async (account: Account) => {
        try {
          // `confirmDeletion` rejette sur « Non » : sans ce try/catch, un refus
          // produirait une promesse rejetée non gérée.
          await serviceModals.confirmDeletion(
            locale('deleteTitle'),
            locale('deleteContent', { email: account.email }),
          )
        } catch (err) {
          return
        }

        lifecycle.setBusy(true)

        try {
          const { administratorDeleteAdminAccount } =
            await graphql.mutations.deleteAdminAccount(account._id)

          if (administratorDeleteAdminAccount.error) {
            fail(administratorDeleteAdminAccount.error)
            return
          }

          lifecycle.setNotice({
            color: 'green',
            text: locale('deleteDone', { email: account.email }),
          })
          await lifecycle.load()
        } catch (err) {
          console.error(err)
          fail('deleteError')
        } finally {
          lifecycle.setBusy(false)
        }
      }

      const reset = async (account: Account) => {
        try {
          // `confirmAction` rejette sur « Non » (onCancel: reject) : sans ce
          // try/catch, un refus produirait une promesse rejetée non gérée.
          await serviceModals.confirmAction(
            locale('resetTitle'),
            locale('resetContent', { email: account.email }),
          )
        } catch (err) {
          return
        }

        try {
          const { administratorTotpReset } = await graphql.mutations.totpReset(
            account._id,
          )

          if (!administratorTotpReset) {
            fail('resetError')
            return
          }

          lifecycle.setNotice({
            color: 'green',
            text: locale('resetDone', { email: account.email }),
          })
          await lifecycle.load()
        } catch (err) {
          console.error(err)
          fail('resetError')
        }
      }

      return (
        <>
          <UI.Stack
            sx={{
              position: 'sticky',
              top: 72,
              zIndex: 10,
              backgroundColor: '#f8f9fc',
            }}
            mx={-16}
            px={16}
            mt={-16}
            py={16}
            spacing={0}
          >
            <UI.Title>
              <UI.Text size={22}>{locale('title')}</UI.Text>
            </UI.Title>
            <UI.Text color="dimmed">{locale('subtitle')}</UI.Text>
          </UI.Stack>

          <UI.Stack
            sx={{ maxWidth: 1280, margin: '2rem auto 32rem' }}
            spacing={32}
          >
            <UI.Card shadow="sm" pb={32} px={24}>
              <UI.Input.Wrapper
                label={<b style={{ fontSize: '2rem' }}>{locale('add')}</b>}
                description={
                  <UI.Text mt={8} size="sm" sx={{ maxWidth: '124rem' }}>
                    {locale('addHelp')}
                  </UI.Text>
                }
              >
                <form onSubmit={createAccount}>
                  <UI.Group mt={24} spacing={8} align="flex-end">
                    <UI.TextInput
                      sx={{ minWidth: 320 }}
                      label={locale('addEmail')}
                      type="email"
                      value={lifecycle.email}
                      disabled={lifecycle.busy}
                      onChange={(event) =>
                        lifecycle.setEmail(event.currentTarget.value)
                      }
                    />
                    <UI.Button
                      type="submit"
                      disabled={lifecycle.busy || !lifecycle.emailIsValid}
                    >
                      {locale('addAction')}
                    </UI.Button>
                  </UI.Group>
                </form>
              </UI.Input.Wrapper>

              {lifecycle.notice && (
                <UI.Alert
                  mt={16}
                  color={lifecycle.notice.color}
                  withCloseButton
                  onClose={() => lifecycle.setNotice(null)}
                >
                  {lifecycle.notice.text}
                </UI.Alert>
              )}

              {lifecycle.invitation && (
                <UI.Alert
                  mt={16}
                  color="blue"
                  title={`${locale('invitationLink')} — ${
                    lifecycle.invitation.email
                  }`}
                  withCloseButton
                  onClose={() => lifecycle.setInvitation(null)}
                >
                  <UI.Group spacing={8}>
                    <UI.Code
                      sx={{
                        fontSize: '1.2rem',
                        maxWidth: 640,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {lifecycle.invitation.url}
                    </UI.Code>
                    <UI.Button
                      size="xs"
                      variant="light"
                      type="button"
                      onClick={lifecycle.copyInvitation}
                    >
                      {locale(lifecycle.copied ? 'copied' : 'copy')}
                    </UI.Button>
                  </UI.Group>
                </UI.Alert>
              )}
            </UI.Card>

            <UI.Card shadow="sm" pb={32} px={24}>
              {lifecycle.loading ? (
                <UI.Center py={32}>
                  <UI.Loader />
                </UI.Center>
              ) : (
                <UI.Table verticalSpacing="sm">
                  <thead>
                    <tr>
                      <th>{locale('email')}</th>
                      <th>{locale('twoFactor')}</th>
                      <th>{locale('devices')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {lifecycle.accounts.map((account) => (
                      <tr key={account._id}>
                        <td>
                          <UI.Text>{account.email}</UI.Text>
                          {account.pending && (
                            <UI.Text size="xs" color="orange">
                              {locale('pending')}
                            </UI.Text>
                          )}
                        </td>
                        <td>
                          <UI.Badge
                            color={account.totpEnabled ? 'green' : 'gray'}
                          >
                            {locale(
                              account.totpEnabled ? 'enabled' : 'disabled',
                            )}
                          </UI.Badge>
                          {account.totpVerifiedAt && (
                            <UI.Text size="xs" color="dimmed" mt={4}>
                              {formatDate(account.totpVerifiedAt)}
                            </UI.Text>
                          )}
                        </td>
                        <td>
                          <UI.Text>{account.trustedDevices}</UI.Text>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <UI.Group spacing={8} position="right" noWrap>
                            {account.pending ? (
                              <UI.Button
                                size="xs"
                                variant="light"
                                disabled={lifecycle.busy}
                                onClick={() => resendInvitation(account)}
                              >
                                {locale('resend')}
                              </UI.Button>
                            ) : (
                              <UI.Button
                                size="xs"
                                variant="light"
                                disabled={!account.totpEnabled}
                                onClick={() => reset(account)}
                              >
                                {locale('reset')}
                              </UI.Button>
                            )}

                            {/* Son propre compte n'est pas supprimable : le
                                bouton n'apparaît pas plutôt que d'échouer. */}
                            {account._id !== lifecycle.moi && (
                              <UI.Button
                                size="xs"
                                variant="subtle"
                                color="red"
                                disabled={lifecycle.busy}
                                onClick={() => supprimer(account)}
                              >
                                {locale('delete')}
                              </UI.Button>
                            )}
                          </UI.Group>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </UI.Table>
              )}
            </UI.Card>
          </UI.Stack>
        </>
      )
    })
